import React from 'react';
import {
  Button as BaseButton,
  ButtonProps as BaseButtonProps,
} from '@mui/base/Button';
import { makeStyles } from 'tss-react/mui';
import Typography, { TypographyVariant } from '@ui/Typography';
import LoadingIcon from '../../../../LoadingIndicators/CircularLoader';

export interface ButtonProps extends BaseButtonProps {
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  typographyVariant?: TypographyVariant;
}

const useStyles = makeStyles<{ fullWidth?: boolean }>()(
  (theme, { fullWidth }) => ({
    root: {
      display: 'flex',
      justifyContent: 'center',
      textDecoration: 'none !important',
      border: 'none',
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(1.625, 2),
      width: fullWidth ? '-webkit-fill-available' : undefined,
      color: theme.palette.ui.button.primary.text.default,
      backgroundColor: theme.palette.ui.button.primary.surface.default,
      '&:hover': {
        backgroundColor: theme.palette.ui.button.primary.surface.hover,
      },
      '@supports ( -moz-appearance:none )': {
        width: fullWidth ? '-moz-available' : undefined,
      },
      '&.base--disabled': {
        opacity: 0.5,
        '&:hover': {
          // Keep original color
          backgroundColor: theme.palette.ui.button.primary.surface.default,
          cursor: 'not-allowed',
        },
      },
      '&.base--focusVisible': {
        outline: `2px solid ${theme.palette.ui.button.primary.surface.default}`,
        outlineOffset: 2,
      },
    },
    secondary: {
      color: theme.palette.ui.button.secondary.text.default,
      backgroundColor: theme.palette.ui.button.secondary.surface.default,
      border:
        theme.palette.mode === 'light'
          ? `2px solid ${theme.palette.ui.button.secondary.border.default}`
          : 'none',
      '&:hover': {
        backgroundColor: theme.palette.ui.button.secondary.surface.hover,
        boxShadow: theme.palette.ui.button.secondary.hover.boxShadow.default,
      },
      '&.base--focusVisible': {
        border:
          theme.palette.mode === 'light'
            ? `2px solid ${theme.palette.ui.button.secondary.focus.border.default}`
            : 'none',
        outline: `2px solid ${theme.palette.ui.button.secondary.focus.outline.default}`,
      },
    },
  })
);

const Button = (props: ButtonProps) => {
  const {
    fullWidth,
    loading,
    className,
    children,
    variant = 'primary',
    typographyVariant,
    ...rest
  } = props;
  const { classes, cx } = useStyles({ fullWidth });

  return (
    <BaseButton
      className={cx(classes.root, className, {
        [classes.secondary]: variant === 'secondary',
      })}
      {...rest}
    >
      {loading && <LoadingIcon />}
      {!loading && (
        <Typography variant={typographyVariant ?? 'pMd'} bold color="inherit">
          {children}
        </Typography>
      )}
    </BaseButton>
  );
};

export default Button;
